import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../music24/hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { Panel } from '../../music24/components/primitives/Panel';
import { SettingsSaveStatus } from './SettingsSaveStatus';
import { useNotificationPreferences } from './notifications/useNotificationPreferences';
import { notificationsMessages } from './notifications/notificationsMessages';
import { PublicIntelligenceGroup } from './notifications/PublicIntelligenceGroup';
import { OtherNotificationGroups } from './notifications/OtherNotificationGroups';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from './settingsCards.module.scss';
import styles from './notifications/notifications.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Settings → Notifications, grouped by the product an email comes from (design settings-redesign
 * c1-c3). Each control saves at once; there is no Save button.
 */
const NotificationsTabComponent: React.FC = () => {
  const intl = useIntl();
  const preferences = useNotificationPreferences();
  const { realtimeAlerts } = useMusic24Capabilities();
  const openUpgradePrompt = useUpgradePrompt();
  const openUpgrade = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.realtimeAlerts), [openUpgradePrompt]);

  if (preferences.error) {
    return <ErrorView error={preferences.error} retry={preferences.refetch} />;
  }
  if (preferences.isLoading || preferences.prefs === null) {
    return <LoadingView />;
  }

  return (
    <div className={cards.pane}>
      {/* One box for where the emails go and the save status: a one-row box, so no head row. */}
      <Panel pad className={styles.leadBox}>
        {preferences.email !== null && (
          <p className={styles.lead} data-clarity-mask="true">
            <FontAwesomeIcon icon={faEnvelope} className={styles.leadIcon} />
            {intl.formatMessage(notificationsMessages.recipient, { email: preferences.email })}
          </p>
        )}
        <div className={styles.leadStatus}>
          <SettingsSaveStatus state={preferences.saveState} />
        </div>
      </Panel>
      <PublicIntelligenceGroup
        prefs={preferences.prefs}
        hasRealtimeAlerts={realtimeAlerts}
        update={preferences.update}
        openUpgrade={openUpgrade}
      />
      <OtherNotificationGroups
        prefs={preferences.prefs}
        update={preferences.update}
        setSaveState={preferences.setSaveState}
      />
    </div>
  );
};

export const NotificationsTab = TypedMemo(NotificationsTabComponent);
